'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@kit/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@kit/ui/drawer';
import { cn } from '@kit/ui/utils';

import { useNativePresentation } from '~/(tenant)/_components/native/_lib/use-native-presentation';
import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';

import type { PublicLocationRow } from '~/(tenant)/book/_lib/server/booking-page.loader';

import type { useVenuePicker } from './use-venue-picker';
import { VenuePickerList } from './venue-picker-list';

interface VenuePickerSurfaceProps {
  picker: ReturnType<typeof useVenuePicker>;
  /** Bottom sheet (phones, native) or centred dialog (desktop web). */
  variant: 'sheet' | 'dialog';
  /** School networks: pick through the /book Region → City → Club funnel. */
  funnelLocations?: PublicLocationRow[];
}

/**
 * The popup that hosts the venue list. Both variants override the global
 * theme with the tenant's native tokens so the picker looks the same in the
 * app, on mobile web and on desktop.
 */
export function VenuePickerSurface({
  picker,
  variant,
  funnelLocations,
}: VenuePickerSurfaceProps) {
  const t = useNativeTokens();
  const presentation = useNativePresentation();
  const surfaceStyle = {
    ...presentation.style,
    background: t.background,
    color: t.foreground,
  };

  if (variant === 'dialog') {
    return (
      <Dialog open={picker.isOpen} onOpenChange={picker.onOpenChange}>
        <DialogContent
          data-native-surface
          data-test="venue-picker-dialog"
          className={cn(
            presentation.className,
            'block max-h-[85vh] overflow-y-auto',
            // The funnel lays clubs out 2-up with photos; plain rows stay narrow.
            funnelLocations ? 'sm:max-w-2xl' : 'sm:max-w-md',
          )}
          style={surfaceStyle}
        >
          <VenuePickerList
            venues={picker.venues}
            activeVenue={picker.activeVenue}
            needsInitialPick={picker.needsInitialPick}
            onPick={picker.pick}
            funnelLocations={funnelLocations}
            Title={DialogTitle}
            Description={DialogDescription}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={picker.isOpen} onOpenChange={picker.onOpenChange}>
      <DrawerContent
        data-native-surface
        data-test="venue-picker-sheet"
        className={presentation.className}
        // Dim + blur the page behind the sheet. Without a scrim the page
        // underneath reads as live, so people tap the back arrow, nothing
        // happens (the drawer is modal), and they assume the app froze.
        blockOutside
        overlayClassName="bg-black/45 backdrop-blur-[2px] dark:bg-black/60"
        // Overrides the drawer's default `bg-background`; the built-in drag
        // handle stays.
        style={surfaceStyle}
      >
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
          style={{ padding: '8px 22px 28px' }}
        >
          <VenuePickerList
            venues={picker.venues}
            activeVenue={picker.activeVenue}
            needsInitialPick={picker.needsInitialPick}
            onPick={picker.pick}
            funnelLocations={funnelLocations}
            Title={DrawerTitle}
            Description={DrawerDescription}
          />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
